// Role-based menu sections + capability matrix.
import {
  LayoutDashboard, Users, Kanban, CheckSquare, MessageSquare, FileText, Mail, Files,
  Zap, FormInput, SlidersHorizontal, Shield, Shuffle, Key, ScrollText, Settings, Globe,
  CalendarClock, BarChart3, Building2, Briefcase, UsersRound, MessageCircle, MessageSquareText, CheckCircle2, ListTree,
} from 'lucide-react';

export const ROLE_LABELS = {
  superadmin: 'SuperAdmin',
  admin: 'Admin',
  supervisor: 'Supervisor',
  ventas: 'Ventas',
  soporte: 'Soporte',
  lectura: 'Sólo lectura',
};

export const ROLE_BADGE_CLASS = {
  superadmin: 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]',
  admin: 'bg-primary/15 text-primary',
  supervisor: 'bg-[hsl(var(--chart-2))]/15 text-[hsl(var(--chart-2))]',
  ventas: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]',
  soporte: 'bg-[hsl(var(--chart-3))]/15 text-[hsl(var(--chart-3))]',
  lectura: 'bg-muted text-muted-foreground',
};

const ALL = [
  { key: 'dashboard', label: 'Inicio', path: '/app/dashboard', icon: LayoutDashboard, section: 'Principal', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
  { key: 'leads', label: 'Leads', path: '/app/leads', icon: Users, section: 'Principal', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
  { key: 'lead-lists', label: 'Listas', path: '/app/lead-lists', icon: ListTree, section: 'Principal', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
  { key: 'companies', label: 'Empresas', path: '/app/companies', icon: Building2, section: 'Principal', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
  { key: 'deals', label: 'Deals', path: '/app/deals', icon: Briefcase, section: 'Principal', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
  { key: 'pipeline', label: 'Pipeline', path: '/app/pipeline', icon: Kanban, section: 'Principal', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
  { key: 'tasks', label: 'Tareas', path: '/app/tasks', icon: CheckSquare, section: 'Principal', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
  { key: 'inbox', label: 'Inbox', path: '/app/inbox', icon: MessageSquare, section: 'Conversaciones', roles: ['admin', 'supervisor', 'ventas', 'soporte'] },
  { key: 'quick-replies', label: 'Respuestas', path: '/app/quick-replies', icon: MessageSquareText, section: 'Conversaciones', roles: ['admin', 'supervisor', 'ventas', 'soporte'] },
  { key: 'close-macros', label: 'Macros', path: '/app/close-macros', icon: CheckCircle2, section: 'Conversaciones', roles: ['admin', 'supervisor'] },
  { key: 'wa-templates', label: 'Plantillas WA', path: '/app/inbox/templates', icon: FileText, section: 'Conversaciones', roles: ['admin', 'supervisor'] },
  { key: 'campaigns', label: 'Campañas', path: '/app/campaigns', icon: CalendarClock, section: 'Conversaciones', roles: ['admin', 'supervisor', 'ventas', 'soporte'] },
  { key: 'email', label: 'Email', path: '/app/email', icon: Mail, section: 'Marketing', roles: ['admin', 'supervisor', 'ventas'] },
  { key: 'email-templates', label: 'Plantillas', path: '/app/email/templates', icon: Files, section: 'Marketing', roles: ['admin', 'supervisor'] },
  { key: 'automations', label: 'Autos', path: '/app/automations', icon: Zap, section: 'Marketing', roles: ['admin', 'supervisor'] },
  { key: 'forms', label: 'Forms', path: '/app/forms', icon: FormInput, section: 'Marketing', roles: ['admin', 'supervisor'] },
  { key: 'webchat', label: 'Webchat', path: '/app/webchat', icon: MessageCircle, section: 'Marketing', roles: ['admin', 'supervisor'] },
  { key: 'reports', label: 'Reportes', path: '/app/reports', icon: BarChart3, section: 'Análisis', roles: ['admin', 'supervisor', 'ventas', 'lectura'] },
  { key: 'audit-logs', label: 'Auditoría', path: '/app/audit-logs', icon: ScrollText, section: 'Análisis', roles: ['admin', 'supervisor'] },
  { key: 'users', label: 'Usuarios', path: '/app/users', icon: Shield, section: 'Administración', roles: ['admin'] },
  { key: 'teams', label: 'Equipos', path: '/app/teams', icon: UsersRound, section: 'Administración', roles: ['admin', 'supervisor'] },
  { key: 'assignment-rules', label: 'Asignación', path: '/app/assignment-rules', icon: Shuffle, section: 'Administración', roles: ['admin', 'supervisor'] },
  { key: 'custom-fields', label: 'Campos', path: '/app/custom-fields', icon: SlidersHorizontal, section: 'Administración', roles: ['admin', 'supervisor'] },
  { key: 'api-keys', label: 'API', path: '/app/api-keys', icon: Key, section: 'Administración', roles: ['admin'] },
  { key: 'settings', label: 'Ajustes', path: '/app/settings', icon: Settings, section: 'Administración', roles: ['admin', 'supervisor', 'ventas', 'soporte', 'lectura'] },
];

export function menuForRole(role) {
  if (!role || role === 'superadmin') return [];
  return ALL.filter((m) => m.roles.includes(role));
}

/** Group flat menu into ordered sections for the sidebar. */
export function menuSectionsForRole(role) {
  const items = menuForRole(role);
  const order = ['Principal', 'Conversaciones', 'Marketing', 'Análisis', 'Administración'];
  const map = new Map();
  for (const m of items) {
    const sec = m.section || 'Principal';
    if (!map.has(sec)) map.set(sec, []);
    map.get(sec).push(m);
  }
  return order.filter((s) => map.has(s)).map((s) => ({ title: s, items: map.get(s) }));
}

export const SUPER_MENU = [
  { key: 'su-dash', label: 'Métricas', path: '/superadmin/dashboard', icon: LayoutDashboard, section: 'Global' },
  { key: 'su-tenants', label: 'Tenants', path: '/superadmin/tenants', icon: Globe, section: 'Global' },
  { key: 'su-smtp', label: 'SMTP', path: '/superadmin/smtp', icon: Mail, section: 'Global' },
  { key: 'su-audit', label: 'Auditoría', path: '/superadmin/audit-logs', icon: ScrollText, section: 'Global' },
];

export function superMenuSections() {
  return [{ title: 'Global', items: SUPER_MENU }];
}

export function canWrite(role) {
  return role && role !== 'lectura';
}

export function canAdmin(role) {
  return role === 'admin';
}

export function canSupervise(role) {
  return role === 'admin' || role === 'supervisor';
}
